/**
 * Team Management Tab - Manage team members and invitations
 *
 * Renders member data directly from CRM ahoy_user entities
 * (not from the legacy MembershipSerializer). Role and integration
 * data come via include_associations on the CRM query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  Copy01,
  Edit01,
  PlusCircle,
  Shield01,
  Trash01,
  User01,
} from '@untitledui/icons';
import type { Invitation, Membership } from 'api-client';
import { ResponseError, TeamsApi } from 'api-client';
import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAdminSeatUpgradeRequests } from '@/components/seat-gate';
import { useBilling } from '@/hooks/billing/useBilling';
import { ORG_MEMBERS_QUERY_KEY, useOrgMembers } from '@/hooks/useOrgMembers';
import { validateInviteEmail } from '@/lib/auth/constants';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Badge, BadgeWithDot } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { formatMoney } from '@/lib/billing/currency';
import { EntityBadge } from '@/lib/crm/components/badges';
import type { EntityBadgeState } from '@/lib/crm/components/badges/types';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { isTransientError } from '@/lib/errors';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { unwrapPropertyValue } from '@/types/crm';

import { useUser } from '../../../../allauth_auth/hooks.js';
import { getApiConfiguration } from '../../../../api/utils';
import { useOrg } from '../../../../contexts/OrgContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import {
  BILLING_ADMIN_PERMISSION,
  TEAMS_ADMIN_PERMISSION,
} from '../../settings/settings-permissions';
import { EditMemberGuidanceModal } from './EditMemberGuidanceModal';

// Inline-link styling shared with other "small text + link to settings" surfaces
// (see CalendarVisibilitySubtitle). Hoisted per FRONTEND_PATTERNS.md "Hoist
// Constants Out of Components" — static value, stable reference.
const MANAGE_SEATS_LINK_CLASS =
  'text-fg-brand-primary hover:underline font-medium';

// Helper note shown below the invite form when the org is on a self-serve
// per-seat tier (per_seat_cents > 0). Informs users that inviting past their
// current seat count automatically provisions a new seat billed prorated.
const SELF_SERVE_SEAT_AUTO_ADD_NOTE =
  'Inviting past your current seats automatically adds a seat (charged today, prorated).';

/**
 * Convert a per-seat amount to its monthly-equivalent value.
 *
 * The team page always surfaces a "per month" price regardless of the org's
 * billing cadence — so the user comparing "what does adding a seat cost me"
 * doesn't have to do mental math on a $948/yr sticker. Yearly cadence ÷ 12
 * matches the pattern used in PlanPickerSection's `formatPerSeatMonthlyPrice`.
 */
function getMonthlyEquivalentCents(
  perSeatCents: number,
  cadence: 'monthly' | 'yearly' | ''
): number {
  return cadence === 'yearly' ? Math.round(perSeatCents / 12) : perSeatCents;
}

// Type for creating invitations - matches the API payload structure
interface InvitationCreate {
  email: string;
  org: number;
  role?: string;
}

// Local augmentation: deactivatedAt and memberKind are now both present on the
// generated api-client Membership type (Membership.ts:114, :127). This local
// shape is retained as the read-only cast used by membershipByEmail; the two
// optional fields below mirror the Membership type so reads compile cleanly.
type MembershipWithDeactivation = {
  id: number;
  userId: number;
  provider?: string | null;
  email?: string;
  deactivatedAt?: string | null;
  memberKind?: 'member' | 'partner' | 'viewer';
  agentGuidance?: string;
};

// Type augmentation for hasActiveSubscription + purchasedSeats (camelCase from api-client Team type)
type OrgWithBilling = {
  hasActiveSubscription?: boolean;
  purchasedSeats?: number | null;
};

/**
 * Extract a raw string property from a CRM object (handles both raw and
 * {value} formats). property value: single-level {value} wrapper — unwrap
 * via unwrapPropertyValue.
 */
function prop(obj: CrmObject, name: string): string {
  const v = obj.properties?.[name];
  if (v == null) {
    return '';
  }
  if (typeof v === 'string') {
    return v;
  }
  return String(unwrapPropertyValue(v) ?? '');
}

/**
 * Resolve a member row's display name with the same precedence regardless of
 * source (CRM `ahoy_user` properties or a membership row): explicit display
 * name, then first+last, then email, then a fallback.
 */
function resolveDisplayName(
  rawDisplayName: string,
  firstName: string,
  lastName: string,
  email: string
): string {
  return (
    rawDisplayName ||
    `${firstName} ${lastName}`.trim() ||
    email ||
    'Unknown User'
  );
}

/** Resolve a CRM user's role info from its `ahoy_user_to_role` association. */
function resolveRoleInfo(
  crmUser: CrmObject,
  associatedObjects: Record<string, CrmObject>
): {
  roleName: string | null;
  roleEntityId: string | null;
  roleResolved: boolean;
} {
  const roleAssoc = crmUser.associations?.find(
    a => a.association_type_name === 'ahoy_user_to_role'
  );
  if (!roleAssoc) {
    return { roleName: null, roleEntityId: null, roleResolved: false };
  }
  const roleEntityId = roleAssoc.target_entity_id;
  const roleObj = associatedObjects[roleEntityId];
  if (!roleObj) {
    return { roleName: null, roleEntityId, roleResolved: false };
  }
  return {
    roleName: prop(roleObj, 'ahoy_display_name') || null,
    roleEntityId,
    roleResolved: true,
  };
}

// =============================================================================
// SeatCapCallout — inline notice shown when the org is at its seat cap.
// Always-visible (no hover required) and surfaces the per-month seat price
// sourced from the live Stripe subscription. Three variants:
//   1. Billing admin + known price → action-oriented copy with price
//   2. Billing admin + unknown price (e.g. custom-quoted tier) → "contact sales"
//   3. Non-billing admin → "ask your billing admin"
// =============================================================================

interface SeatCapCalloutProps {
  seatCount: number;
  canManageBilling: boolean;
  /** Per-seat amount IN THE ORG'S BILLING CADENCE, sourced from Stripe via useBilling. */
  perSeatCents: number | null;
  /** 'monthly' | 'yearly' | ''. Used only to convert yearly → monthly equivalent. */
  cadence: 'monthly' | 'yearly' | '';
}

function SeatCapCallout({
  seatCount,
  canManageBilling,
  perSeatCents,
  cadence,
}: SeatCapCalloutProps) {
  // Monthly-equivalent price. Yearly cadence is divided by 12 so the user
  // never sees a $948/yr sticker — always a per-month figure for easy comparison.
  const monthlyPrice =
    perSeatCents != null && perSeatCents > 0
      ? formatMoney(getMonthlyEquivalentCents(perSeatCents, cadence), 'USD')
      : null;

  // Compact title + actionable description — matches notifications-section's
  // AlertFloating usage. Body splits into "what" and "how to fix".
  const title = `You've used all ${seatCount} seats`;
  let description: string;
  if (!canManageBilling) {
    description =
      'Ask your billing admin to add seats, or remove a teammate to free one up.';
  } else if (monthlyPrice == null) {
    // Custom-quoted tier (Growth) — self-serve seat add isn't supported.
    // The user IS the billing admin in this branch, so direct them at sales
    // rather than telling them to "contact their billing admin" (i.e. themselves).
    description =
      'Contact sales to add seats, or remove a teammate to free one up.';
  } else {
    description = `Add another seat for ${monthlyPrice}/mo (charged today, prorated) or remove a teammate to free one up.`;
  }

  return (
    <AlertFloating color='warning' title={title} description={description} />
  );
}

// =============================================================================
// InviteFormContent — inline form. When at-cap and the user can manage
// billing, the primary action morphs from "Invite" into "Add seat & invite"
// (which opens a confirmation modal in the parent). Otherwise the original
// disabled-with-tooltip behavior is preserved.
// =============================================================================

interface InviteFormContentProps {
  newMemberEmail: string;
  setNewMemberEmail: (v: string) => void;
  inviteLoading: boolean;
  inviteSuccess: boolean;
  /** The email address that was just invited, used in the success alert copy. Null when no recent invite. */
  invitedEmail: string | null;
  handleInviteMember: () => Promise<void>;
  /** True when active members + pending invites would exceed purchased seats. */
  atCap: boolean;
  /**
   * When true, the form's submit action becomes "Add seat & invite" — calls
   * `handleRequestAddSeatAndInvite` instead of `handleInviteMember`. Set by
   * the parent only when at-cap AND the user can manage billing AND we have
   * a known per-seat price.
   */
  morphToAddSeat: boolean;
  handleRequestAddSeatAndInvite: () => void;
  /** True while the seat-bump API call is in flight (disables the button). */
  seatChangeLoading: boolean;
  /**
   * True when the org members fetch failed. Capacity can't be trusted, so the
   * invite action is disabled with a retry-prompt tooltip (the parent renders a
   * retry banner above the form).
   */
  membersUnavailable: boolean;
}

function InviteFormContent({
  newMemberEmail,
  setNewMemberEmail,
  inviteLoading,
  inviteSuccess,
  invitedEmail,
  handleInviteMember,
  atCap,
  morphToAddSeat,
  handleRequestAddSeatAndInvite,
  seatChangeLoading,
  membersUnavailable,
}: InviteFormContentProps) {
  const emailTrimmed = newMemberEmail.trim();
  const emailError =
    emailTrimmed.length > 0 ? validateInviteEmail(emailTrimmed) : undefined;
  const isEmailValid = emailTrimmed.length > 0 && emailError === undefined;
  // When morphing, the button is enabled at-cap (so the user can add a seat).
  // Otherwise atCap still disables the original Invite button. `membersUnavailable`
  // disables the submit unconditionally: capacity can't be trusted on a failed
  // fetch. The parent forces `atCap` true on error to cover the subscribed path,
  // but orgs without an active subscription return `atCap` false before that
  // guard runs — so we re-check here to keep the tooltip's "paused" message
  // honest in every billing state.
  const submitDisabled =
    inviteLoading ||
    seatChangeLoading ||
    !isEmailValid ||
    membersUnavailable ||
    (atCap && !morphToAddSeat);

  let inviteDisabledTooltip: string | undefined;
  if (membersUnavailable) {
    inviteDisabledTooltip =
      "Couldn't load team members – retry above to send invites";
  } else if (atCap && !morphToAddSeat) {
    inviteDisabledTooltip =
      'Ask your billing admin to add seats or remove a member';
  } else if (
    !inviteLoading &&
    !seatChangeLoading &&
    !isEmailValid &&
    emailTrimmed.length === 0
  ) {
    inviteDisabledTooltip = 'Add an email to send an invite';
  }

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!isEmailValid) {
        return;
      }
      if (membersUnavailable) {
        return;
      }
      if (atCap && !morphToAddSeat) {
        return;
      }
      if (morphToAddSeat) {
        handleRequestAddSeatAndInvite();
        return;
      }
      void handleInviteMember();
    },
    [
      isEmailValid,
      membersUnavailable,
      atCap,
      morphToAddSeat,
      handleRequestAddSeatAndInvite,
      handleInviteMember,
    ]
  );

  let buttonLabel: string;
  if (inviteLoading) {
    buttonLabel = 'Inviting…';
  } else if (seatChangeLoading) {
    buttonLabel = 'Adding seat…';
  } else if (morphToAddSeat) {
    buttonLabel = 'Add seat & invite';
  } else {
    buttonLabel = 'Invite';
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      {/*
       * Side-by-side input + button at matching heights, one size down from
       * settings default to keep the row visually compact.
       *
       * No `py` override needed: Button and Input share one height ladder, so
       * `size='sm'` is 40px for both. This row previously carried a `py='lg'`
       * on the button to cancel the two scales' line-height delta by hand —
       * that workaround is what prompted aligning them at the source.
       */}
      <div className='flex items-center gap-3'>
        <div className='flex-1'>
          <Input
            type='email'
            placeholder='colleague@company.com'
            value={newMemberEmail}
            onChange={setNewMemberEmail}
            size='sm'
            aria-label='Email address'
          />
        </div>
        <Button
          type='submit'
          color='primary'
          size='sm'
          isDisabled={submitDisabled}
          disabledTooltip={inviteDisabledTooltip}
          iconLeading={
            !inviteLoading && !seatChangeLoading ? PlusCircle : undefined
          }
        >
          {buttonLabel}
        </Button>
      </div>

      {emailError && (
        <p className='text-sm text-[var(--color-text-error-primary)]'>
          {emailError}
        </p>
      )}

      {inviteSuccess && (
        <AlertFloating
          color='success'
          title='Invitation sent'
          description={
            invitedEmail
              ? `Invite sent to ${invitedEmail} – it appears under pending invitations below.`
              : undefined
          }
        />
      )}
    </form>
  );
}

// =============================================================================
// TeamMemberRow — single team-member row with stable per-row callbacks. Lives
// at module scope so the parent's `.map(crmUser => ...)` doesn't recreate the
// inline `onClick` / `onKeyDown` / `state` literals on every render.
// =============================================================================

interface TeamMemberRowProps {
  entityId: string;
  email: string;
  displayName: string;
  roleName: string | null;
  roleEntityId: string | null;
  roleResolved: boolean;
  membership:
    | {
        id: number;
        userId: number;
        provider: string | null;
        deactivatedAt: string | null;
        agentGuidance?: string;
        memberKind?: 'member' | 'partner' | 'viewer' | null;
      }
    | undefined;
  isCurrentUser: boolean;
  canModify: boolean;
  canManageMembers: boolean;
  atCapForReactivation: boolean;
  openSlideout: (entityId: string) => void;
  onRemoveMember: (member: { id: number; name: string }) => void;
  onReactivate: (membershipId: number) => void;
  onMakeAdmin: (member: { id: number; name: string }) => void;
  onMakeUser: (member: { id: number; name: string }) => void;
  onEditGuidance: (member: {
    id: number;
    name: string;
    currentGuidance: string;
  }) => void;
  /** Admin-only: true when this viewer has a pending full-seat upgrade request. */
  hasPendingUpgradeRequest?: boolean;
}

function TeamMemberRow({
  entityId,
  email,
  displayName,
  roleName,
  roleEntityId,
  roleResolved,
  membership,
  isCurrentUser,
  canModify,
  canManageMembers,
  atCapForReactivation,
  openSlideout,
  onRemoveMember,
  onReactivate,
  onMakeAdmin,
  onMakeUser,
  onEditGuidance,
  hasPendingUpgradeRequest = false,
}: TeamMemberRowProps) {
  const initials = displayName.charAt(0).toUpperCase();

  const isDeactivated = membership?.deactivatedAt != null;

  // Google OAuth sign-in (from membership provider).
  const hasGoogle = membership?.provider === 'google';

  const handleClick = useCallback(() => {
    openSlideout(entityId);
  }, [openSlideout, entityId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openSlideout(entityId);
      }
    },
    [openSlideout, entityId]
  );

  const handleStopPropagation = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    },
    []
  );

  let roleVariant: 'Admin' | 'User' | 'custom';
  if (roleName === 'Admin') {
    roleVariant = 'Admin';
  } else if (roleName === 'User') {
    roleVariant = 'User';
  } else {
    roleVariant = 'custom';
  }

  const isViewer = membership?.memberKind === 'viewer';

  const handleRemove = useCallback(() => {
    if (!membership) {
      return;
    }
    onRemoveMember({ id: membership.id, name: displayName });
  }, [membership, onRemoveMember, displayName]);

  const handleReactivate = useCallback(() => {
    if (!membership) {
      return;
    }
    onReactivate(membership.id);
  }, [membership, onReactivate]);

  const handleMakeAdmin = useCallback(() => {
    if (!membership) {
      return;
    }
    onMakeAdmin({ id: membership.id, name: displayName });
  }, [membership, onMakeAdmin, displayName]);

  const handleMakeUser = useCallback(() => {
    if (!membership) {
      return;
    }
    onMakeUser({ id: membership.id, name: displayName });
  }, [membership, onMakeUser, displayName]);

  const handleEditGuidance = useCallback(() => {
    if (!membership) {
      return;
    }
    onEditGuidance({
      id: membership.id,
      name: displayName,
      currentGuidance: membership.agentGuidance ?? '',
    });
  }, [membership, onEditGuidance, displayName]);

  // Stable badge state — same identity unless the role data actually changed.
  const roleBadgeState = useMemo<EntityBadgeState>(
    () => ({
      kind: 'data',
      data: {
        id: roleEntityId ?? '',
        entityType: 'ahoy_role',
        displayName: roleName || 'No Role',
      },
    }),
    [roleEntityId, roleName]
  );

  return (
    <div
      className='flex items-center gap-4 p-4 border border-border-secondary rounded-lg hover:bg-bg-secondary transition-colors cursor-pointer'
      onClick={handleClick}
      role='button'
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <Avatar size='md' initials={initials} />

      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2'>
          <span className='font-medium text-primary truncate'>
            {displayName}
          </span>
          {isCurrentUser && (
            <Badge color='gray' size='sm'>
              You
            </Badge>
          )}
          {isDeactivated && canManageMembers && (
            <Badge color='gray' size='sm'>
              Deactivated
            </Badge>
          )}
          {membership?.memberKind === 'viewer' && (
            <Badge color='gray' size='sm'>
              Viewer
            </Badge>
          )}
          {hasPendingUpgradeRequest && (
            <Badge color='brand' size='sm'>
              Requested a full seat
            </Badge>
          )}
        </div>
        <div className='flex items-center gap-1.5 text-sm text-tertiary'>
          {email ? (
            <>
              {hasGoogle && (
                <svg
                  className='w-4 h-4 flex-shrink-0'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                    fill='#4285F4'
                  />
                  <path
                    d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                    fill='#34A853'
                  />
                  <path
                    d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                    fill='#FBBC05'
                  />
                  <path
                    d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                    fill='#EA4335'
                  />
                </svg>
              )}
              <span className='truncate'>{email}</span>
            </>
          ) : (
            <span>No email</span>
          )}
        </div>
      </div>

      <div className='flex items-center gap-2 flex-shrink-0'>
        <EntityBadge
          state={roleBadgeState}
          variant='default'
          interactive={roleResolved && roleEntityId != null}
        />

        {isDeactivated && canManageMembers && membership && (
          <div onClick={handleStopPropagation}>
            <Button
              color='secondary'
              size='sm'
              onClick={handleReactivate}
              isDisabled={atCapForReactivation}
              disabledTooltip={
                atCapForReactivation
                  ? 'Upgrade plan or remove a member first'
                  : undefined
              }
            >
              Reactivate
            </Button>
          </div>
        )}

        {canModify && membership && (
          <div onClick={handleStopPropagation}>
            <Dropdown.Root>
              <Dropdown.DotsButton />
              <Dropdown.Popover>
                <Dropdown.Menu>
                  {(roleVariant === 'User' || isViewer) && (
                    <Dropdown.Item icon={Shield01} onAction={handleMakeAdmin}>
                      Make admin
                    </Dropdown.Item>
                  )}
                  {(roleVariant === 'Admin' || isViewer) && (
                    <Dropdown.Item icon={User01} onAction={handleMakeUser}>
                      Make user
                    </Dropdown.Item>
                  )}
                  {!isDeactivated && (
                    <Dropdown.Item icon={Edit01} onAction={handleEditGuidance}>
                      Edit agent guidance
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item icon={Trash01} onAction={handleRemove}>
                    Remove member
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </div>
        )}
      </div>
    </div>
  );
}

// Informational only — partners have support access; not a manageable teammate.
const PartnerMemberRow = memo(function PartnerMemberRow({
  membership,
}: {
  membership: Membership;
}) {
  const email = membership.email ?? '';
  const displayName =
    membership.displayName ||
    `${membership.firstName ?? ''} ${membership.lastName ?? ''}`.trim() ||
    email ||
    'Partner';
  const initials = displayName.charAt(0).toUpperCase();
  return (
    <div className='flex items-center gap-4 p-4 border border-border-secondary rounded-lg'>
      <Avatar size='md' initials={initials} />
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2'>
          <span className='font-medium text-primary truncate'>
            {displayName}
          </span>
          <Badge color='brand' size='sm'>
            Partner
          </Badge>
        </div>
        <div className='flex items-center gap-1.5 text-sm text-tertiary'>
          {email ? (
            <span className='truncate'>{email}</span>
          ) : (
            <span>No email</span>
          )}
        </div>
      </div>
    </div>
  );
});

// =============================================================================
// InvitationRowActions — extracted to avoid inline callbacks in invitations.map()
// =============================================================================

interface InvitationRowActionsProps {
  invitation: { id?: string };
  copiedInvitationId: string | null;
  handleCopyInviteLink: (id: string) => Promise<void> | void;
  onRemoveInvitation: (id: string) => void;
}

const InvitationRowActions = memo(function InvitationRowActions({
  invitation,
  copiedInvitationId,
  handleCopyInviteLink,
  onRemoveInvitation,
}: InvitationRowActionsProps) {
  const handleCopy = useCallback(() => {
    if (invitation.id) {
      handleCopyInviteLink(invitation.id);
    }
  }, [invitation.id, handleCopyInviteLink]);

  const handleRemoveAction = useCallback(() => {
    if (invitation.id) {
      onRemoveInvitation(invitation.id);
    }
  }, [invitation.id, onRemoveInvitation]);

  return (
    <div className='flex items-center gap-2 flex-shrink-0'>
      <Button
        color={
          copiedInvitationId === invitation.id ? 'brand-soft' : 'secondary'
        }
        size='sm'
        iconLeading={<Copy01 className='w-4 h-4' />}
        onClick={handleCopy}
      >
        {copiedInvitationId === invitation.id ? 'Copied' : 'Copy Invite Link'}
      </Button>
      <Dropdown.Root>
        <Dropdown.DotsButton />
        <Dropdown.Popover>
          <Dropdown.Menu>
            <Dropdown.Item icon={Trash01} onAction={handleRemoveAction}>
              Remove
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>
    </div>
  );
});

export function TeamManagementTab() {
  const { currentOrg, refreshCurrentOrg } = useOrg();
  const user = useUser();
  const {
    members: orgMembers,
    isLoading: orgMembersLoading,
    isError: orgMembersError,
    error: orgMembersErrorObj,
    refetch: refetchOrgMembers,
  } = useOrgMembers(currentOrg?.id);
  // Only a *hard* members failure forces the seat lockout. A transient blip
  // that still leaves us with last-known member data must NOT disable
  // invite/reactivate. But when we have no member data at all (first load),
  // keep the fail-safe so capacity is never decided from an empty list.
  const orgMembersHardError =
    orgMembersError &&
    (!isTransientError(orgMembersErrorObj) || orgMembers.length === 0);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [operationError, setOperationError] = useState<string | null>(null);
  // Optional supplementary copy rendered below the error title when an
  // operation needs to give the user a concrete next step (e.g. "use Copy
  // invite link to share manually"). Always updated via
  // `setOperationErrorState` so a later error never inherits stale
  // description text from a previous operation.
  const [operationErrorDescription, setOperationErrorDescription] = useState<
    string | null
  >(null);
  const setOperationErrorState = useCallback(
    (message: string | null, description: string | null = null) => {
      setOperationError(message);
      setOperationErrorDescription(description);
    },
    []
  );
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(
    null
  );
  const [deletingMemberId, setDeletingMemberId] = useState<number | null>(null);

  // Refs for timeout cleanup
  const inviteSuccessTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const copiedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Modal state
  const [showMakeAdminModal, setShowMakeAdminModal] = useState(false);
  const [makeAdminTarget, setMakeAdminTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [roleChangeLoading, setRoleChangeLoading] = useState<number | null>(
    null
  );
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [showRemoveInvitationModal, setShowRemoveInvitationModal] =
    useState(false);
  const [showAddSeatModal, setShowAddSeatModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<string | null>(
    null
  );
  // Snapshot the email at the moment the user clicks "Add seat & invite" so
  // editing the input mid-confirmation doesn't change what gets invited.
  const [pendingInviteEmail, setPendingInviteEmail] = useState<string | null>(
    null
  );
  const [guidanceTarget, setGuidanceTarget] = useState<{
    id: number;
    name: string;
    currentGuidance: string;
  } | null>(null);
  const [guidanceSaving, setGuidanceSaving] = useState(false);
  const [guidanceError, setGuidanceError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const teamsApi = useMemo(() => new TeamsApi(getApiConfiguration()), []);
  const { hasPermission, hasPermissionGroup } = usePermissions();
  const { openSlideout } = useSlideoutStack();
  const isAdmin = hasPermission('ahoy_role', 'ahoy_property_updated');
  // Invite gating uses the `ahoy_permissions_admin` group — the same capability
  // the backend enforces on the invite endpoint (TeamModelAccessPermissions ->
  // ahoy_team/ahoy_property_updated) and that onboarding checks
  // (apps/onboarding/tasks/team_invite.py). The previous
  // `hasPermission('ahoy_user', 'ahoy_object_created')` check gated on a
  // permission no role grants — user entities are created as principals, never
  // via an ahoy_object_created event — so the invite form was hidden for
  // everyone, including full admins (#see team-invite gating fix).
  const canInvite = hasPermissionGroup(TEAMS_ADMIN_PERMISSION);
  // Billing settings are gated by the same permission group used in
  // settings-nav-config — only surface the "Manage seats" affordance to
  // users who can actually access the billing section (avoids dead links).
  const canManageBilling = hasPermissionGroup(BILLING_ADMIN_PERMISSION);

  // Admin-only: which viewer memberships have a pending full-seat upgrade request.
  // Fetched only for admin sessions so viewer sessions never trigger this request.
  const { data: adminUpgradeData } = useAdminSeatUpgradeRequests(
    currentOrg?.id,
    isAdmin
  );
  const pendingUpgradeMembershipIds = useMemo(
    () =>
      new Set(
        (adminUpgradeData?.pending_requests ?? []).map(r => r.membership_id)
      ),
    [adminUpgradeData]
  );

  // Live subscription state from Stripe. Used to surface the per-seat price
  // in the at-cap callout and to drive the "Add seat & invite" morph.
  // Source of truth for `per_seat_cents` + `billing_cadence` — the Team type
  // on `currentOrg` only carries `purchasedSeats`/`hasActiveSubscription`.
  // We catch `changeSeats` errors in `handleConfirmAddSeatAndInvite` and route
  // them through `operationError`, so we don't read useBilling's internal
  // `seatChangeError` state here (would double-render the same message).
  const { subscription, changeSeats, seatChangeLoading } = useBilling(
    currentOrg?.id ?? 0
  );

  // ----- CRM query: ahoy_user entities with role + integration associations -----
  const crmUsersQuery = useQuery(
    crmObjectListSentinelQuery(
      currentOrg?.id ?? 0,
      'ahoy_user',
      {
        properties: [
          'ahoy_first_name',
          'ahoy_last_name',
          'ahoy_email',
          'ahoy_display_name',
          'ahoy_avatar_url',
          // Stable identity used to exclude partner ahoy_user records from the
          // Members list even when the partner's email is blank (see below).
          'ahoy_user_id',
        ],
        limit: 200,
      },
      {
        include_associations: {
          ahoy_user_to_role: { properties: ['ahoy_display_name'] },
        },
      }
    )
  );
  const crmUsersList = useStoreBackedList(crmUsersQuery);

  const { results: crmUsers, associatedObjects } = crmUsersList;

  // Dedupe active partners so a partner who appears more than once (e.g.
  // transiently also a crmUser pre-#2) renders as a single informational row.
  // A `Map` keeps the last membership per key. The key is the lowercased email
  // when present, otherwise the membership id — so multiple blank-email partners
  // stay distinct rows instead of collapsing into one under a shared '' key.
  const partnerMembers = useMemo(
    () =>
      Array.from(
        new Map(
          orgMembers
            .filter(m => m.memberKind === 'partner' && m.deactivatedAt == null)
            .map(
              m =>
                [
                  m.email ? `email:${m.email.toLowerCase()}` : `id:${m.id}`,
                  m,
                ] as const
            )
        ).values()
      ),
    [orgMembers]
  );
  // Exclusion sets are built from ALL partner memberships (including
  // deactivated), not just `partnerMembers`. A deactivated partner is hidden
  // from the Partners section (via `partnerMembers`), but its CRM `ahoy_user`
  // may still be present; excluding it here keeps it out of the teammate list
  // too, so a partner never leaks in as an ordinary member.
  //
  // We match a CRM `ahoy_user` to a partner membership on two keys: the stable
  // `ahoy_user_id` (the Django user id, stamped onto the entity — see
  // apps/orgs/tasks.py) and, as a fallback, the lowercased email. The user-id
  // key is what catches a blank-email partner, whose email key ('') is dropped
  // to avoid excluding unrelated blank-email members.
  //
  // Compared NUMERICALLY, not as strings: `ahoy_user_id` is a CRM number
  // property, so the API serializes it with decimal places ("6.000000"), while
  // `membership.userId` is a plain int. A string compare of '6' vs '6.000000'
  // never matches, which silently made this guard dead code.
  const partnerUserIdSet = useMemo(
    () =>
      new Set(
        orgMembers
          .filter(m => m.memberKind === 'partner')
          .map(m => Number(m.userId))
          .filter(n => Number.isFinite(n))
      ),
    [orgMembers]
  );
  const partnerEmailSet = useMemo(
    () =>
      new Set(
        orgMembers
          .filter(m => m.memberKind === 'partner')
          .map(m => (m.email ?? '').toLowerCase())
          .filter(Boolean)
      ),
    [orgMembers]
  );
  const memberCrmUsers = useMemo(
    () =>
      crmUsers.filter(u => {
        const rawUserId = prop(u, 'ahoy_user_id');
        const userId = rawUserId ? Number(rawUserId) : NaN;
        if (Number.isFinite(userId) && partnerUserIdSet.has(userId)) {
          return false;
        }
        const email = prop(u, 'ahoy_email').toLowerCase();
        return !(email && partnerEmailSet.has(email));
      }),
    [crmUsers, partnerEmailSet, partnerUserIdSet]
  );

  // Viewer memberships project their CRM `ahoy_user` with ahoy_hidden=true
  // (issue #13412 KD4), so the user-scoped crm-objects query never returns
  // it — a viewer never appears in `crmUsers`/`memberCrmUsers` above. Render
  // viewer rows directly from the memberships list instead, the same source
  // `partnerMembers` already uses. Deduped the same way partnerMembers is.
  const viewerMembers = useMemo(
    () =>
      Array.from(
        new Map(
          orgMembers
            .filter(m => m.memberKind === 'viewer' && m.deactivatedAt == null)
            .map(
              m =>
                [
                  m.email ? `email:${m.email.toLowerCase()}` : `id:${m.id}`,
                  m,
                ] as const
            )
        ).values()
      ),
    [orgMembers]
  );

  // Map email → membership (for delete endpoint, "You" badge, auth provider, and deactivation)
  const membershipByEmail = useMemo(() => {
    const map = new Map<
      string,
      {
        id: number;
        userId: number;
        provider: string | null;
        deactivatedAt: string | null;
        agentGuidance?: string;
        memberKind?: 'member' | 'partner' | 'viewer' | null;
      }
    >();
    for (const m of orgMembers as MembershipWithDeactivation[]) {
      if (m.email) {
        map.set(m.email.toLowerCase(), {
          id: m.id,
          userId: m.userId,
          provider: m.provider ?? null,
          deactivatedAt: m.deactivatedAt ?? null,
          agentGuidance: m.agentGuidance,
          memberKind: m.memberKind ?? null,
        });
      }
    }
    return map;
  }, [orgMembers]);

  const billing = (currentOrg ?? {}) as OrgWithBilling;

  const activeMemberCount = useMemo(() => {
    if (orgMembersLoading && orgMembers.length === 0) {
      // Sync fallback prevents atCap flashing false during initial load.
      return currentOrg?.memberCount ?? 0;
    }
    const members = orgMembers as MembershipWithDeactivation[];
    // Partners and viewers don't consume a seat — exclude both from seat math.
    return members.filter(
      m =>
        m.deactivatedAt == null &&
        m.memberKind !== 'partner' &&
        m.memberKind !== 'viewer'
    ).length;
  }, [orgMembers, orgMembersLoading, currentOrg?.memberCount]);

  const pendingInvitationCount = invitations.length;

  const atCap = useMemo(() => {
    if (!billing.hasActiveSubscription || billing.purchasedSeats == null) {
      return false;
    }
    // A hard members-fetch failure means `activeMemberCount` can't be trusted
    // (either a non-transient error, or no last-known data to fall back on).
    // Treat the org as at-cap to block invites until valid membership data
    // loads — capacity decisions must never be made from bad data (the retry
    // banner prompts the user to reload).
    if (orgMembersHardError) {
      return true;
    }
    return (
      activeMemberCount + pendingInvitationCount + 1 > billing.purchasedSeats
    );
  }, [
    billing.hasActiveSubscription,
    billing.purchasedSeats,
    activeMemberCount,
    pendingInvitationCount,
    orgMembersHardError,
  ]);

  // Reactivation only consumes a seat from active members. Pending invitations
  // do not count toward the cap on the backend, so they must not block valid
  // reactivations here either.
  const atCapForReactivation = useMemo(() => {
    if (!billing.hasActiveSubscription || billing.purchasedSeats == null) {
      return false;
    }
    // Same guard as `atCap`: a hard members fetch failure means we can't
    // trust the active-member count, so block reactivation rather than risk
    // exceeding cap.
    if (orgMembersHardError) {
      return true;
    }
    return activeMemberCount + 1 > billing.purchasedSeats;
  }, [
    billing.hasActiveSubscription,
    billing.purchasedSeats,
    activeMemberCount,
    orgMembersHardError,
  ]);

  const handleRequestRemoveMember = useCallback(
    (member: { id: number; name: string }) => {
      setSelectedMember(member);
      setShowRemoveMemberModal(true);
    },
    []
  );

  const handleRequestRemoveInvitation = useCallback((id: string) => {
    setSelectedInvitation(id);
    setShowRemoveInvitationModal(true);
  }, []);

  const handleSetNewMemberEmail = useCallback(
    (v: string) => setNewMemberEmail(v),
    [setNewMemberEmail]
  );

  const loadInvitations = useCallback(async () => {
    if (!currentOrg?.id) {
      return;
    }

    try {
      setLoading(true);
      const invitationsResponse = await teamsApi.invitationsList2({
        orgId: currentOrg.id,
      });
      setInvitations(invitationsResponse.results || []);
    } catch (err: unknown) {
      logger.error(
        'Error loading invitations',
        err instanceof Error ? err : new Error(String(err)),
        {
          org_id: currentOrg.id,
        }
      );
      setOperationErrorState("We couldn't load invitations");
    } finally {
      setLoading(false);
    }
  }, [currentOrg, teamsApi, setOperationErrorState]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (inviteSuccessTimeoutRef.current) {
        clearTimeout(inviteSuccessTimeoutRef.current);
      }
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Send an invitation for a specific email. Extracted from the original
   * form-state-driven flow so the seat-bump-then-invite path can call it
   * with a snapshotted email (independent of the live input).
   */
  const sendInvitationForEmail = useCallback(
    async (email: string) => {
      if (!currentOrg?.id) {
        return;
      }

      try {
        setInviteLoading(true);
        setInviteSuccess(false);
        setOperationErrorState(null);

        const invitationPayload: InvitationCreate = {
          email: email.trim(),
          org: currentOrg.id,
          role: 'member',
        };

        await teamsApi.invitationsCreate2({
          orgId: currentOrg.id,
          invitation: invitationPayload as Omit<
            Invitation,
            'id' | 'invited_by'
          >,
        });

        setInviteSuccess(true);
        setInvitedEmail(email.trim());

        // Clear existing timeout before setting new one
        if (inviteSuccessTimeoutRef.current) {
          clearTimeout(inviteSuccessTimeoutRef.current);
        }
        inviteSuccessTimeoutRef.current = setTimeout(() => {
          setInviteSuccess(false);
          setInvitedEmail(null);
        }, 3000);
      } catch (err: unknown) {
        logger.error(
          'Error inviting member',
          err instanceof Error ? err : new Error(String(err)),
          {
            org_id: currentOrg.id,
          }
        );
        throw err;
      } finally {
        setInviteLoading(false);
        // Clear the input on every completion — success OR failure. The
        // backend creates the Invitation row before attempting to send the
        // email, so even a 500-after-save (e.g. SES rejecting the address)
        // still results in a saved invite that's visible below. Retaining
        // the typed value would only invite a duplicate. The pending list
        // (refreshed just below) is the source of truth for "did the invite
        // land", so the input doesn't need to persist as a fallback.
        setNewMemberEmail('');
        // ALWAYS refresh the pending-invitation list — success OR failure.
        // Reason: the backend creates the Invitation row BEFORE attempting
        // to send the email, so an SES/email-delivery failure still leaves
        // the invite saved. Reloading on failure makes that row visible so
        // the user can grab "Copy invite link" and share it manually
        // instead of being told "try again" and ending up with a duplicate.
        try {
          await loadInvitations();
        } catch (reloadErr) {
          logger.warn('Failed to reload invitations after invite attempt', {
            org_id: currentOrg.id,
            error:
              reloadErr instanceof Error
                ? reloadErr.message
                : String(reloadErr),
          });
        }
      }
    },
    [currentOrg, teamsApi, loadInvitations, setOperationErrorState]
  );

  const handleInviteMember = useCallback(async () => {
    try {
      await sendInvitationForEmail(newMemberEmail);
    } catch (err: unknown) {
      // A seat-cap rejection means no Invitation row was created at all (the
      // cap is enforced before create), so the generic "check the list below"
      // fallback below doesn't apply — surface the backend's specific reason
      // instead, mirroring handleReactivateMember's error_code handling.
      if (err instanceof ResponseError) {
        try {
          const data = await err.response.json();
          if (data?.error_code === 'seat_cap_exceeded') {
            setOperationErrorState(
              'Seat limit reached',
              data.detail ||
                'Seat limit reached. Upgrade your plan or remove a member.'
            );
            return;
          }
        } catch {
          // Response body wasn't JSON — fall through to the generic message.
        }
      }
      // Error already logged by sendInvitationForEmail. The invite row may
      // still exist in the DB (e.g. the email send failed but the Invitation
      // was saved before that). The finally block in sendInvitationForEmail
      // has already refreshed the list, so the user can spot the saved row
      // and use "Copy invite link" to share it manually.
      setOperationErrorState(
        "We couldn't send the invitation email",
        'If the invite appears in the list below, use "Copy invite link" to share it manually. Otherwise, please try again.'
      );
    }
  }, [newMemberEmail, sendInvitationForEmail, setOperationErrorState]);

  // Retry the members fetch after a failure. `refetchOrgMembers` is a stable
  // React Query reference, so this callback never churns.
  const handleRetryOrgMembers = useCallback(() => {
    void refetchOrgMembers();
  }, [refetchOrgMembers]);

  // ----- Add-seat-and-invite flow ---------------------------------------------
  // Morph the Invite button into "Add seat & invite" the moment we know the
  // org is at-cap. `atCap` is derived purely from `currentOrg` (sync, already
  // loaded before this page renders), so the morph is INSTANT — no waiting on
  // the /billing/subscription/ fetch.
  //
  // The price display (callout + modal) DOES depend on the Stripe subscription
  // and gates separately on `subscription != null`. Decoupling the two lets
  // the button's identity reflect the state we already know without lagging
  // behind the price fetch.
  //
  // Custom-quoted tiers (Growth, `per_seat_cents = null`) almost never hit a
  // self-serve cap in practice — and if one does, the confirmation modal
  // catches it with a "contact your billing admin" message + disabled confirm.
  // When the members fetch failed, `atCap` is forced true as a safety block.
  // Suppress the morph in that case so the button stays a disabled "Invite"
  // (with a retry-prompt tooltip) instead of a clickable "Add seat & invite"
  // that would charge for a seat based on an unknown member count.
  const morphToAddSeat = atCap && canManageBilling && !orgMembersHardError;

  // True only when we have a real, programmatic per-seat price from Stripe.
  // Drives the modal's confirm-button enable state and the callout's price copy.
  const hasKnownSeatPrice =
    subscription != null &&
    subscription.per_seat_cents != null &&
    subscription.per_seat_cents > 0;

  // Body content for the confirmation modal — three states depending on
  // whether the subscription has resolved (and whether the tier has a
  // self-serve seat price at all). Built as a ReactNode so we can use
  // structured copy (bolded price + email) via the dialog's `children` slot,
  // rather than the prose-string `description` prop.
  let addSeatBody: ReactNode;
  if (subscription == null) {
    addSeatBody = (
      <p>Loading your subscription details – this should only take a moment.</p>
    );
  } else if (hasKnownSeatPrice) {
    const monthly = formatMoney(
      getMonthlyEquivalentCents(
        // Narrowing — hasKnownSeatPrice already verified non-null.
        subscription.per_seat_cents as number,
        subscription.billing_cadence
      ),
      'USD'
    );
    addSeatBody = (
      <div className='space-y-2'>
        <p>
          Adds 1 seat at{' '}
          <span className='font-semibold text-fg-primary'>{monthly}/mo</span>,
          prorated for the rest of your current billing period.
        </p>
        <p>
          Sends an invitation to{' '}
          <span className='font-medium text-fg-primary'>
            {pendingInviteEmail}
          </span>
          .
        </p>
      </div>
    );
  } else {
    // Custom-quoted tier (Growth) — self-serve seat add isn't supported.
    addSeatBody = (
      <p>
        This plan is custom-quoted, so seats can&apos;t be added through
        self-serve. Contact your billing admin or sales to extend your seat
        count.
      </p>
    );
  }
  // Confirm is only valid when we have a price. Loading + Growth both disable.
  const addSeatConfirmDisabled = !hasKnownSeatPrice;

  const handleRequestAddSeatAndInvite = useCallback(() => {
    const trimmed = newMemberEmail.trim();
    if (!trimmed) {
      return;
    }
    setOperationErrorState(null);
    setPendingInviteEmail(trimmed);
    setShowAddSeatModal(true);
  }, [newMemberEmail, setOperationErrorState]);

  const handleCancelAddSeat = useCallback(() => {
    setShowAddSeatModal(false);
    setPendingInviteEmail(null);
  }, []);

  const handleConfirmAddSeatAndInvite = useCallback(async () => {
    if (!currentOrg?.id || !pendingInviteEmail || !subscription) {
      return;
    }
    const emailToInvite = pendingInviteEmail;
    const currentSeats = subscription.purchased_seats;
    try {
      setOperationErrorState(null);
      await changeSeats(currentSeats + 1);
    } catch (err: unknown) {
      logger.error(
        'Failed to add seat before invite',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: currentOrg.id, current_seats: currentSeats }
      );
      setOperationErrorState("We couldn't add a seat", 'Try again.');
      // Leave the modal open so the user can retry; cancel still available.
      return;
    }

    // Seat added — refresh the org so purchasedSeats / cap state is fresh,
    // then fire the invite. Distinct error handling: a failed invite at this
    // point means the seat IS purchased but no email went out. Tell the user
    // exactly that so they don't double-bump trying again.
    try {
      await refreshCurrentOrg();
    } catch (refreshErr) {
      logger.warn('refreshCurrentOrg failed after seat bump (non-fatal)', {
        org_id: currentOrg.id,
        error:
          refreshErr instanceof Error ? refreshErr.message : String(refreshErr),
      });
    }
    await queryClient.invalidateQueries({
      queryKey: ORG_MEMBERS_QUERY_KEY(user?.id ?? null, currentOrg.id),
    });

    try {
      await sendInvitationForEmail(emailToInvite);
      setShowAddSeatModal(false);
      setPendingInviteEmail(null);
    } catch {
      // sendInvitationForEmail already logged the error AND already refreshed
      // the pending-invitation list in its finally block, so the user can spot
      // the saved invite below and copy its link. Tailor the message for the
      // seat-bump path so they know they won't be charged twice on retry.
      setOperationErrorState(
        "Seat added – but the invitation email didn't send",
        'Use "Copy invite link" below to share it manually. You won\'t be charged a second time if you retry the invite.'
      );
      setShowAddSeatModal(false);
      setPendingInviteEmail(null);
    }
  }, [
    currentOrg,
    pendingInviteEmail,
    subscription,
    changeSeats,
    refreshCurrentOrg,
    sendInvitationForEmail,
    setOperationErrorState,
    queryClient,
    user,
  ]);

  const handleCopyInviteLink = useCallback(
    async (invitationId: string) => {
      try {
        const inviteUrl = `${window.location.origin}/invitation/${invitationId}`;
        await navigator.clipboard.writeText(inviteUrl);
        setCopiedInvitationId(invitationId);

        // Clear existing timeout before setting new one
        if (copiedTimeoutRef.current) {
          clearTimeout(copiedTimeoutRef.current);
        }
        copiedTimeoutRef.current = setTimeout(
          () => setCopiedInvitationId(null),
          2000
        );
      } catch (err: unknown) {
        logger.error(
          'Error copying invitation link',
          err instanceof Error ? err : new Error(String(err)),
          {
            org_id: currentOrg?.id,
          }
        );
        setOperationErrorState("We couldn't copy the invitation link.");
      }
    },
    [currentOrg, setOperationErrorState]
  );

  const handleRemoveInvitation = useCallback(async () => {
    if (!currentOrg?.id || !selectedInvitation) {
      return;
    }

    try {
      setOperationErrorState(null);
      await teamsApi.invitationsDestroy2({
        orgId: currentOrg.id,
        id: selectedInvitation,
      });
      await loadInvitations();
      setShowRemoveInvitationModal(false);
      setSelectedInvitation(null);
    } catch (err: unknown) {
      logger.error(
        'Error removing invitation',
        err instanceof Error ? err : new Error(String(err)),
        {
          org_id: currentOrg.id,
          invitation_id: selectedInvitation,
        }
      );
      setOperationErrorState("We couldn't remove the invitation. Try again.");
      setShowRemoveInvitationModal(false);
    }
  }, [
    currentOrg,
    selectedInvitation,
    teamsApi,
    loadInvitations,
    setOperationErrorState,
  ]);

  const handleRemoveMember = useCallback(async () => {
    if (!currentOrg?.id || !selectedMember) {
      return;
    }

    try {
      setOperationErrorState(null);
      setDeletingMemberId(selectedMember.id);

      const response = await fetchWithDefaults(
        `/api/orgs/${currentOrg.id}/memberships/${selectedMember.id}/`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let errorMsg = "We couldn't remove this member. Try again.";
        try {
          const data = JSON.parse(text);
          errorMsg = data.detail || data.error || errorMsg;
        } catch {
          // Use default error message
        }
        throw new Error(errorMsg);
      }

      await refreshCurrentOrg();
      await queryClient.invalidateQueries({
        queryKey: ORG_MEMBERS_QUERY_KEY(user?.id ?? null, currentOrg.id),
      });
      setShowRemoveMemberModal(false);
    } catch (err: unknown) {
      logger.error(
        'Error removing member',
        err instanceof Error ? err : new Error(String(err)),
        {
          org_id: currentOrg.id,
          membership_id: selectedMember.id,
        }
      );
      setOperationErrorState("We couldn't remove that member");
      setShowRemoveMemberModal(false);
    } finally {
      setDeletingMemberId(null);
    }
  }, [
    currentOrg,
    selectedMember,
    refreshCurrentOrg,
    setOperationErrorState,
    queryClient,
    user,
  ]);

  const callSetRole = useCallback(
    async (membershipId: number, roleName: 'Admin' | 'User') => {
      if (!currentOrg?.id) {
        return;
      }
      try {
        setOperationErrorState(null);
        setRoleChangeLoading(membershipId);
        const response = await fetchWithDefaults(
          `/api/orgs/${currentOrg.id}/memberships/${membershipId}/set_role/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ role_name: roleName }),
          }
        );
        if (!response.ok) {
          const text = await response.text();
          let data: {
            detail?: string;
            error?: string;
            error_code?: string;
            role_name?: string[];
          } | null = null;
          try {
            data = JSON.parse(text);
          } catch {
            // non-JSON body — fall through to the generic message
          }
          if (data?.error_code === 'seat_cap_exceeded') {
            setOperationErrorState(
              'Seat limit reached',
              data.detail ||
                'Seat limit reached. Upgrade your plan or remove a member.'
            );
            return;
          }
          if (data?.error_code === 'viewer_downgrade_not_supported') {
            logger.error(
              'Viewer downgrade not supported',
              new Error(data.error || 'viewer_downgrade_not_supported'),
              {
                org_id: currentOrg.id,
                membership_id: membershipId,
                role_name: roleName,
                error_detail: data.error,
              }
            );
            setOperationErrorState(
              "We couldn't change that role",
              data.error || undefined
            );
            return;
          }
          let errorMsg = "We couldn't change this role. Try again.";
          const fieldError = Array.isArray(data?.role_name)
            ? data?.role_name[0]
            : undefined;
          errorMsg = data?.detail || data?.error || fieldError || errorMsg;
          throw new Error(errorMsg);
        }
        // Invalidate the CRM users list query so the role badge updates without a page refresh.
        await queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(currentOrg.id, 'ahoy_user'),
        });
        // Invalidate the membership list query so memberKind/badges update without a page refresh.
        await queryClient.invalidateQueries({
          queryKey: ORG_MEMBERS_QUERY_KEY(user?.id ?? null, currentOrg.id),
        });
      } catch (err: unknown) {
        logger.error(
          'Error changing member role',
          err instanceof Error ? err : new Error(String(err)),
          {
            org_id: currentOrg.id,
            membership_id: membershipId,
            role_name: roleName,
          }
        );
        setOperationErrorState("We couldn't change that role");
      } finally {
        setRoleChangeLoading(null);
      }
    },
    [currentOrg, queryClient, setOperationErrorState, user]
  );

  const handleRequestMakeAdmin = useCallback(
    (member: { id: number; name: string }) => {
      setMakeAdminTarget(member);
      setShowMakeAdminModal(true);
    },
    []
  );

  const handleConfirmMakeAdmin = useCallback(async () => {
    if (!makeAdminTarget) {
      return;
    }
    await callSetRole(makeAdminTarget.id, 'Admin');
    setShowMakeAdminModal(false);
    setMakeAdminTarget(null);
  }, [makeAdminTarget, callSetRole]);

  const handleCancelMakeAdmin = useCallback(() => {
    setShowMakeAdminModal(false);
    setMakeAdminTarget(null);
  }, []);

  const handleMakeUserInstant = useCallback(
    (member: { id: number; name: string }) => {
      void callSetRole(member.id, 'User');
    },
    [callSetRole]
  );

  const handleReactivateMember = useCallback(
    async (membershipId: number) => {
      if (!currentOrg?.id) {
        return;
      }
      try {
        setOperationErrorState(null);
        const response = await fetchWithDefaults(
          `/api/orgs/${currentOrg.id}/memberships/${membershipId}/reactivate/`,
          { method: 'POST' }
        );
        if (!response.ok) {
          const text = await response.text();
          let errorMsg = "We couldn't reactivate this member. Try again.";
          try {
            const data = JSON.parse(text);
            if (data.error_code === 'seat_cap_exceeded') {
              errorMsg =
                data.detail ||
                'Seat limit reached. Upgrade your plan or remove a member.';
            } else {
              errorMsg = data.detail || data.error || errorMsg;
            }
          } catch {
            // Use default error message
          }
          throw new Error(errorMsg);
        }
        await refreshCurrentOrg();
        await queryClient.invalidateQueries({
          queryKey: ORG_MEMBERS_QUERY_KEY(user?.id ?? null, currentOrg.id),
        });
      } catch (err: unknown) {
        logger.error(
          'Error reactivating member',
          err instanceof Error ? err : new Error(String(err)),
          { org_id: currentOrg.id, membership_id: membershipId }
        );
        setOperationErrorState("We couldn't reactivate that member");
      }
    },
    [currentOrg, refreshCurrentOrg, setOperationErrorState, queryClient, user]
  );

  const handleCancelRemoveMember = useCallback(
    () => setShowRemoveMemberModal(false),
    []
  );

  const handleCancelRemoveInvitation = useCallback(() => {
    setShowRemoveInvitationModal(false);
    setSelectedInvitation(null);
  }, []);

  const handleRequestEditGuidance = useCallback(
    (member: { id: number; name: string; currentGuidance: string }) => {
      setGuidanceError(null);
      setGuidanceTarget(member);
    },
    []
  );

  const handleCancelEditGuidance = useCallback(() => {
    setGuidanceTarget(null);
    setGuidanceError(null);
  }, []);

  const handleSaveGuidance = useCallback(
    async (guidance: string) => {
      if (!currentOrg?.id || !guidanceTarget) {
        return;
      }
      try {
        setGuidanceSaving(true);
        setGuidanceError(null);
        const response = await fetchWithDefaults(
          `/api/orgs/${currentOrg.id}/memberships/${guidanceTarget.id}/`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ agent_guidance: guidance }),
          }
        );
        if (!response.ok) {
          const text = await response.text();
          let msg = "We couldn't save the agent guidance. Try again.";
          try {
            const data = JSON.parse(text);
            const fieldErr = Array.isArray(data.agent_guidance)
              ? data.agent_guidance[0]
              : undefined;
            msg = data.detail || fieldErr || msg;
          } catch {
            // Use default error message
          }
          throw new Error(msg);
        }
        await queryClient.invalidateQueries({
          queryKey: ORG_MEMBERS_QUERY_KEY(user?.id ?? null, currentOrg.id),
        });
        setGuidanceTarget(null);
      } catch (err: unknown) {
        logger.error(
          'Error saving member agent guidance',
          err instanceof Error ? err : new Error(String(err)),
          { org_id: currentOrg.id, membership_id: guidanceTarget.id }
        );
        setGuidanceError("We couldn't save that guidance");
      } finally {
        setGuidanceSaving(false);
      }
    },
    [currentOrg, guidanceTarget, queryClient, user]
  );

  if (loading || (orgMembersLoading && orgMembers.length === 0)) {
    return (
      <div className='flex justify-center items-center py-12'>
        <LoadingIndicator size='lg' />
      </div>
    );
  }

  if (!currentOrg) {
    return <div>No organization selected</div>;
  }

  let membersContent: ReactNode;
  if (crmUsersList.isLoading) {
    membersContent = (
      <div className='flex justify-center items-center py-8'>
        <LoadingIndicator size='md' />
      </div>
    );
  } else if (memberCrmUsers.length > 0 || viewerMembers.length > 0) {
    membersContent = (
      <div className='space-y-2'>
        {memberCrmUsers.map(crmUser => {
          const email = prop(crmUser, 'ahoy_email');
          const membership = email
            ? membershipByEmail.get(email.toLowerCase())
            : undefined;
          const isCurrentUser = membership?.userId === user?.id;
          const canModify = isAdmin && !isCurrentUser;
          const displayName = resolveDisplayName(
            prop(crmUser, 'ahoy_display_name'),
            prop(crmUser, 'ahoy_first_name'),
            prop(crmUser, 'ahoy_last_name'),
            email
          );
          const { roleName, roleEntityId, roleResolved } = resolveRoleInfo(
            crmUser,
            associatedObjects
          );
          return (
            <TeamMemberRow
              key={crmUser.entity_id}
              entityId={crmUser.entity_id}
              email={email}
              displayName={displayName}
              roleName={roleName}
              roleEntityId={roleEntityId}
              roleResolved={roleResolved}
              membership={membership}
              isCurrentUser={isCurrentUser}
              canModify={canModify}
              canManageMembers={isAdmin}
              atCapForReactivation={atCapForReactivation}
              openSlideout={openSlideout}
              onRemoveMember={handleRequestRemoveMember}
              onReactivate={handleReactivateMember}
              onMakeAdmin={handleRequestMakeAdmin}
              onMakeUser={handleMakeUserInstant}
              onEditGuidance={handleRequestEditGuidance}
            />
          );
        })}
        {viewerMembers.map(viewerMember => {
          const isCurrentUser = viewerMember.userId === user?.id;
          const canModify = isAdmin && !isCurrentUser;
          const displayName = resolveDisplayName(
            viewerMember.displayName,
            viewerMember.firstName,
            viewerMember.lastName,
            viewerMember.email
          );
          return (
            <TeamMemberRow
              key={`viewer-${viewerMember.id}`}
              entityId={viewerMember.entityId}
              email={viewerMember.email}
              displayName={displayName}
              roleName={null}
              roleEntityId={null}
              roleResolved={false}
              membership={{
                id: viewerMember.id,
                userId: viewerMember.userId,
                provider: viewerMember.provider,
                deactivatedAt: viewerMember.deactivatedAt ?? null,
                agentGuidance: viewerMember.agentGuidance,
                memberKind: viewerMember.memberKind ?? null,
              }}
              isCurrentUser={isCurrentUser}
              canModify={canModify}
              canManageMembers={isAdmin}
              atCapForReactivation={atCapForReactivation}
              openSlideout={openSlideout}
              onRemoveMember={handleRequestRemoveMember}
              onReactivate={handleReactivateMember}
              onMakeAdmin={handleRequestMakeAdmin}
              onMakeUser={handleMakeUserInstant}
              onEditGuidance={handleRequestEditGuidance}
              hasPendingUpgradeRequest={pendingUpgradeMembershipIds.has(
                viewerMember.id
              )}
            />
          );
        })}
      </div>
    );
  } else if (partnerMembers.length === 0) {
    membersContent = (
      <p className='text-sm text-fg-tertiary'>
        No teammates yet – invite someone above to get started.
      </p>
    );
  } else {
    membersContent = null; // only partners exist; Partners section renders below
  }

  return (
    <div className='space-y-8'>
      {operationError && (
        <AlertFloating
          color='error'
          title={operationError}
          description={operationErrorDescription ?? ''}
        />
      )}

      {/* Members-fetch failure: seat-capacity checks can't be trusted, so we
          surface a distinct error state with a retry. While this is shown,
          invite + reactivation actions stay disabled (atCap is forced true). */}
      {orgMembersHardError && (
        <AlertFloating
          color='error'
          title="Couldn't load team members"
          description='Seat usage is unavailable, so inviting and reactivating are paused. Retry to continue.'
          confirmLabel='Retry'
          onConfirm={handleRetryOrgMembers}
        />
      )}

      {/* Invitations sub-section — bare layout (no card) to match the
          settings-page convention (notifications-section.tsx, currency-
          section.tsx). The outer page wrapper (TeamMembersSection) already
          provides the h2 "Team members" + page subtitle, so this surface
          uses an h3 sub-header instead of stacking a second h2. */}
      {canInvite && (
        <div className='space-y-4'>
          <div className='flex items-center justify-between gap-4'>
            <h3 className='text-base font-semibold text-fg-primary'>
              Invite a teammate
            </h3>
            {/* Seat counter floats to the right of the sub-header so it
                stays visible without taking up its own row. */}
            {billing.hasActiveSubscription &&
              billing.purchasedSeats != null &&
              !orgMembersHardError && (
                <span className='text-sm text-fg-tertiary'>
                  {activeMemberCount} of {billing.purchasedSeats} seats used
                  {pendingInvitationCount > 0 &&
                    ` (${pendingInvitationCount} pending)`}
                  {canManageBilling && (
                    <>
                      {' · '}
                      <Link
                        to={`/${currentOrg.id}/settings/billing`}
                        className={MANAGE_SEATS_LINK_CLASS}
                      >
                        Manage seats
                      </Link>
                    </>
                  )}
                </span>
              )}
          </div>

          <InviteFormContent
            newMemberEmail={newMemberEmail}
            setNewMemberEmail={handleSetNewMemberEmail}
            inviteLoading={inviteLoading}
            inviteSuccess={inviteSuccess}
            invitedEmail={invitedEmail}
            handleInviteMember={handleInviteMember}
            atCap={atCap}
            morphToAddSeat={morphToAddSeat}
            handleRequestAddSeatAndInvite={handleRequestAddSeatAndInvite}
            seatChangeLoading={seatChangeLoading}
            membersUnavailable={orgMembersHardError}
          />

          {/* Self-serve seat auto-add note — shown only when the org has an
              active subscription AND is on a self-serve tier (per_seat_cents > 0).
              The invite path only auto-bumps seats for subscribed orgs
              (has_active_subscription), so gating on that flag keeps this promise
              aligned with the behavior the admin will actually get. Custom-quoted
              tiers leave this absent and rely on the SeatCapCallout's "contact
              sales" path instead. */}
          {subscription?.has_active_subscription === true &&
            hasKnownSeatPrice && (
              <p className='text-xs text-fg-tertiary'>
                {SELF_SERVE_SEAT_AUTO_ADD_NOTE}
              </p>
            )}

          {/* At-cap callout — surfaces the per-seat monthly price (sourced
              from Stripe via useBilling) and the path forward. Rendered
              BELOW the email field and only when the user has started
              typing. Gated on `subscription != null` so we don't flash
              wrong copy in the brief window before the real price arrives. */}
          {atCap &&
            billing.purchasedSeats != null &&
            newMemberEmail.trim().length > 0 &&
            subscription != null && (
              <SeatCapCallout
                seatCount={billing.purchasedSeats}
                canManageBilling={canManageBilling}
                perSeatCents={subscription.per_seat_cents}
                cadence={subscription.billing_cadence}
              />
            )}

          {/* Pending Invitations list — bordered rows, matches the
              APIKeysSection.tsx pattern (line 75). */}
          {invitations.length > 0 && (
            <div className='space-y-3 pt-2'>
              <h4 className='text-sm font-medium text-fg-primary'>
                Pending invitations
              </h4>
              <div className='space-y-2'>
                {invitations.map(invitation => (
                  <div
                    key={invitation.id}
                    className='flex items-center justify-between gap-3 p-4 border border-border-secondary rounded-lg'
                  >
                    <div className='flex items-center gap-3 flex-1 min-w-0'>
                      <div className='w-9 h-9 bg-warning-50 rounded-full flex items-center justify-center flex-shrink-0'>
                        <Clock className='w-4 h-4 text-warning-600' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='font-medium text-fg-primary text-sm truncate'>
                            {invitation.email}
                          </span>
                          <BadgeWithDot color='warning' size='sm'>
                            Pending
                          </BadgeWithDot>
                        </div>
                        <div className='text-xs text-fg-tertiary'>
                          Invited by {invitation.invitedBy}
                        </div>
                      </div>
                    </div>

                    <InvitationRowActions
                      invitation={invitation}
                      copiedInvitationId={copiedInvitationId}
                      handleCopyInviteLink={handleCopyInviteLink}
                      onRemoveInvitation={handleRequestRemoveInvitation}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Members sub-section — flat layout (no card). Plain "(N)" count
          in the h3 itself, no Badge chrome. */}
      <div className='space-y-4'>
        <h3 className='text-base font-semibold text-fg-primary'>
          Members ({memberCrmUsers.length + viewerMembers.length})
        </h3>

        {membersContent}
      </div>

      {partnerMembers.length > 0 && (
        <div className='space-y-4'>
          <div>
            <h3 className='text-base font-semibold text-fg-primary'>
              Partners
            </h3>
            <p className='text-sm text-fg-tertiary'>
              People with support access to your workspace. They can&apos;t be
              assigned work or managed like a teammate.
            </p>
          </div>
          <div className='space-y-2'>
            {partnerMembers.map(m => (
              <PartnerMemberRow key={m.id} membership={m} />
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationDialog
        isOpen={showMakeAdminModal}
        title='Make admin?'
        description={`${makeAdminTarget?.name ?? 'They'} will gain full administrative access – including the ability to manage schemas, permissions, and other admins.`}
        variant='default'
        confirmLabel='Make admin'
        cancelLabel='Cancel'
        isLoading={roleChangeLoading === makeAdminTarget?.id}
        onConfirm={handleConfirmMakeAdmin}
        onCancel={handleCancelMakeAdmin}
      />

      <ConfirmationDialog
        isOpen={showRemoveMemberModal}
        title='Remove team member?'
        description={`${selectedMember?.name} will immediately lose access to this organization and all its data.`}
        variant='destructive'
        confirmLabel='Remove member'
        cancelLabel='Cancel'
        isLoading={deletingMemberId === selectedMember?.id}
        onConfirm={handleRemoveMember}
        onCancel={handleCancelRemoveMember}
      />

      <ConfirmationDialog
        isOpen={showRemoveInvitationModal}
        title='Remove invitation?'
        description='This invitation will be cancelled and the invite link will no longer work.'
        variant='destructive'
        confirmLabel='Remove Invitation'
        cancelLabel='Cancel'
        isLoading={false}
        onConfirm={handleRemoveInvitation}
        onCancel={handleCancelRemoveInvitation}
      />

      {/* Add-seat confirmation. The modal can open before the Stripe
          subscription has loaded (the morph button is intentionally instant);
          when that happens we show a brief "Loading…" description and keep
          confirm disabled until the price arrives. Custom-quoted tiers also
          land here with confirm disabled. */}
      {/* Title is short ("Add a seat?") — the invitee email + cost live in
          the body so the title stays compact regardless of address length. */}
      <ConfirmationDialog
        isOpen={showAddSeatModal}
        title='Add a seat?'
        variant='default'
        confirmLabel='Add seat & invite'
        cancelLabel='Cancel'
        isLoading={seatChangeLoading || inviteLoading}
        isConfirmDisabled={addSeatConfirmDisabled}
        onConfirm={handleConfirmAddSeatAndInvite}
        onCancel={handleCancelAddSeat}
      >
        {addSeatBody}
      </ConfirmationDialog>

      <EditMemberGuidanceModal
        isOpen={guidanceTarget !== null}
        memberName={guidanceTarget?.name ?? ''}
        currentGuidance={guidanceTarget?.currentGuidance ?? ''}
        isSaving={guidanceSaving}
        errorMessage={guidanceError}
        onSave={handleSaveGuidance}
        onCancel={handleCancelEditGuidance}
      />
    </div>
  );
}
